import { useEffect, useRef } from "react";
import {
    Brush,
    Bug,
    Droplet,
    Eraser,
    FileOutput,
    Frame,
    Image as ImageIcon,
    Pencil,
    Shapes,
    Trash2,
    Upload,
} from "lucide-react";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";
import { isValidSvg } from "../../utils/svgUtils";
import { CheckboxField } from "../reusable/CheckboxField";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { ColorPaletteEditor } from "../reusable/ColorPaletteEditor";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { UndoRedoControls } from "../reusable/UndoRedoControls";
import {
    useArtbrushSvgCanvas,
    type PenPointerSnapshot,
} from "./ArtbrushSvgCanvasContext";
import "./ArtbrushSvgCanvas.scss";

function fmt(n: number, digits = 3): string {
    if (!Number.isFinite(n)) return "—";
    return n.toFixed(digits);
}

function PenDebugReadout({ s }: { s: PenPointerSnapshot }) {
    const rows: [string, string][] = [
        ["Phase", s.phase],
        ["Pressed (btn 0)", s.pressed ? "yes" : "no"],
        ["Pointer type", s.pointerType],
        ["Pointer id", String(s.pointerId)],
        ["Button", String(s.button)],
        ["Buttons (mask)", `0x${s.buttons.toString(16)} (${s.buttons})`],
        ["Is primary", s.isPrimary ? "yes" : "no"],
        ["Client X/Y", `${fmt(s.clientX, 1)}, ${fmt(s.clientY, 1)}`],
        [
            "SVG X/Y",
            s.svgX != null && s.svgY != null
                ? `${fmt(s.svgX, 2)}, ${fmt(s.svgY, 2)}`
                : "—",
        ],
        ["Pressure", fmt(s.pressure, 4)],
        ["Tilt X / Y", `${fmt(s.tiltX, 2)}° / ${fmt(s.tiltY, 2)}°`],
        ["Twist", fmt(s.twist, 2)],
        ["Tangential pressure", fmt(s.tangentialPressure, 4)],
        ["Contact W × H", `${fmt(s.width, 2)} × ${fmt(s.height, 2)}`],
        ["Coalesced (last move)", String(s.coalescedCount)],
        ["Time (ms)", fmt(s.timestamp, 1)],
    ];

    return (
        <dl className="artbrush-pen-debug-readout">
            {rows.map(([label, value]) => (
                <div key={label} className="artbrush-pen-debug-row">
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                </div>
            ))}
        </dl>
    );
}

export function ArtbrushSvgCanvasControls() {
    const {
        brushSize,
        setBrushSize,
        minPressureFactor,
        setMinPressureFactor,
        brushStrokeCount,
        setBrushStrokeCount,
        brushMaxSpreadPx,
        setBrushMaxSpreadPx,
        strokeSmoothing,
        setStrokeSmoothing,
        eraserSize,
        setEraserSize,
        color,
        setColor,
        recentColors,
        hasContent,
        downloadSvg,
        referenceImage,
        referenceLoadError,
        showReferenceImage,
        setShowReferenceImage,
        referenceOpacity,
        setReferenceOpacity,
        includeReferenceInExport,
        setIncludeReferenceInExport,
        resizeCanvasToReferenceImage,
        setResizeCanvasToReferenceImage,
        referenceInputRef,
        handleReferenceImageLoad,
        clearReferenceImage,
        setReferenceDimensions,
        tool,
        setTool,
        stampDistance,
        setStampDistance,
        stampObjectSize,
        setStampObjectSize,
        stampPressureSizeEnabled,
        setStampPressureSizeEnabled,
        stampPressureMinFactor,
        setStampPressureMinFactor,
        stampObjectPlacementMode,
        setStampObjectPlacementMode,
        stampPerpendicularScatterPx,
        setStampPerpendicularScatterPx,
        stampColorEnabled,
        setStampColorEnabled,
        stampColorMode,
        setStampColorMode,
        stampColorSingle,
        setStampColorSingle,
        stampColorPalette,
        setStampColorPalette,
        stampBaseRotationDeg,
        setStampBaseRotationDeg,
        stampRotationMode,
        setStampRotationMode,
        stampPolarRotationCenterXPct,
        setStampPolarRotationCenterXPct,
        stampPolarRotationCenterYPct,
        setStampPolarRotationCenterYPct,
        stampMergeUnionOnStrokeEnd,
        setStampMergeUnionOnStrokeEnd,
        stampObjectInputRef,
        objectSvgs,
        removeStampObjectAt,
        setStampObjectFromPreset,
        handleStampObjectFileLoad,
        parsedStampObjects,
        canvasWidth,
        canvasHeight,
        setCanvasWidth,
        setCanvasHeight,
        clearCanvas,
        undo,
        redo,
        canUndo,
        canRedo,
        resetParameters,
        showPenDebug,
        setShowPenDebug,
        penSnapshot,
        clearPenSnapshot,
    } = useArtbrushSvgCanvas();

    const colorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        return () => {
            if (colorTimeoutRef.current) clearTimeout(colorTimeoutRef.current);
        };
    }, []);

    const handleColorPickerChange = (next: string) => {
        if (colorTimeoutRef.current) clearTimeout(colorTimeoutRef.current);
        colorTimeoutRef.current = setTimeout(() => {
            setColor(next);
        }, 200);
    };

    const handleClear = () => {
        if (!hasContent) return;
        if (!window.confirm("Clear all strokes on the canvas?")) return;
        clearCanvas();
    };

    const clampDim = (n: number, min = 100, max = 8192) =>
        Math.max(min, Math.min(max, Math.round(n)));

    const handleCanvasWidth = (v: string | number) => {
        const w = clampDim(typeof v === "number" ? v : Number(v));
        if (!Number.isFinite(w)) return;
        setCanvasWidth(w);
    };

    const handleCanvasHeight = (v: string | number) => {
        const h = clampDim(typeof v === "number" ? v : Number(v));
        if (!Number.isFinite(h)) return;
        setCanvasHeight(h);
    };

    const clampRefDim = (n: number) => clampDim(n, 1, 8192);

    const handleRefWidth = (v: string | number) => {
        if (!referenceImage) return;
        const w = clampRefDim(typeof v === "number" ? v : Number(v));
        if (!Number.isFinite(w)) return;
        setReferenceDimensions(w, referenceImage.height);
    };

    const handleRefHeight = (v: string | number) => {
        if (!referenceImage) return;
        const h = clampRefDim(typeof v === "number" ? v : Number(v));
        if (!Number.isFinite(h)) return;
        setReferenceDimensions(referenceImage.width, h);
    };

    return (
        <div className="panel-controls artbrush-svg-canvas-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Artbrush SVG Canvas</h2>
                    <ToolDescriptionPopover dialogLabel="Artbrush SVG Canvas tool description">
                        <p>
                            Draw variable-width strokes on an SVG canvas. Brush
                            diameter scales with pen pressure; mouse and touch
                            use full pressure.
                        </p>
                        <p>
                            Strokes are filled paths built from pressure samples
                            (coalesced pointer events for smooth lines).
                        </p>
                        <p>
                            <strong>Reference images</strong> are layered over
                            the paper for tracing. They are not included in SVG
                            export unless you enable{" "}
                            <strong>Include reference in export</strong>.
                        </p>
                        <p>
                            <strong>Download SVG</strong> exports the paper and
                            strokes (including one in progress). Pen debug
                            overlays are never included.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={downloadSvg}
                        title="Download SVG matching the canvas (paper + strokes, no debug overlay)"
                    >
                        <FileOutput size={16} aria-hidden />
                        Download SVG
                    </button>
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={handleClear}
                        disabled={!hasContent}
                    >
                        Clear canvas
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <CollapsibleToolGroup title="Canvas" icon={Frame} defaultOpen>
                    <div className="field-row artbrush-ref-size-row">
                        <label className="field-label">
                            Width
                            <DebouncedInput
                                type="number"
                                min={100}
                                max={8192}
                                step={1}
                                value={canvasWidth}
                                onChange={handleCanvasWidth}
                            />
                        </label>
                        <label className="field-label">
                            Height
                            <DebouncedInput
                                type="number"
                                min={100}
                                max={8192}
                                step={1}
                                value={canvasHeight}
                                onChange={handleCanvasHeight}
                            />
                        </label>
                    </div>
                    <p className="control-panel-hint">
                        Sets the artboard and exported SVG size (100–8192).
                        Existing strokes keep their coordinates; zoom only
                        affects the preview.
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="Reference image"
                    icon={ImageIcon}
                    defaultOpen
                >
                    <input
                        ref={referenceInputRef}
                        type="file"
                        accept="image/*"
                        className="visually-hidden"
                        onChange={handleReferenceImageLoad}
                    />
                    <button
                        type="button"
                        className="tool-file-btn"
                        onClick={() => referenceInputRef.current?.click()}
                    >
                        <Upload size={16} aria-hidden />
                        {referenceImage ? "Replace image" : "Upload image"}
                    </button>
                    {referenceImage && (
                        <p
                            className="tool-file-name"
                            title={referenceImage.name}
                        >
                            {referenceImage.name}
                        </p>
                    )}
                    {referenceLoadError && (
                        <p className="tool-error">{referenceLoadError}</p>
                    )}
                    <div className="field-row field-row--switch">
                        <CheckboxField
                            checked={resizeCanvasToReferenceImage}
                            onChange={setResizeCanvasToReferenceImage}
                            label="Resize canvas to image size on import"
                            inline
                            title="Sets canvas width and height to the imported image dimensions (100–8192). Reference fills the canvas."
                        />
                    </div>
                    {referenceImage && (
                        <>
                            <div className="field-row field-row--switch">
                                <CheckboxField
                                    checked={showReferenceImage}
                                    onChange={setShowReferenceImage}
                                    label="Show on canvas"
                                    inline
                                />
                            </div>
                            <div className="field-row artbrush-ref-size-row">
                                <label className="field-label">
                                    Image width
                                    <DebouncedInput
                                        type="number"
                                        min={1}
                                        max={8192}
                                        step={1}
                                        value={Math.round(referenceImage.width)}
                                        onChange={handleRefWidth}
                                    />
                                </label>
                                <label className="field-label">
                                    Image height
                                    <DebouncedInput
                                        type="number"
                                        min={1}
                                        max={8192}
                                        step={1}
                                        value={Math.round(
                                            referenceImage.height,
                                        )}
                                        onChange={handleRefHeight}
                                    />
                                </label>
                            </div>
                            <p className="control-panel-hint">
                                Reference size in canvas units; stays centered
                                when adjusted.
                            </p>
                            <div className="field-row">
                                <label className="field-label">
                                    Opacity:{" "}
                                    {Math.round(referenceOpacity * 100)}%
                                    <input
                                        type="range"
                                        min={5}
                                        max={100}
                                        step={1}
                                        value={Math.round(
                                            referenceOpacity * 100,
                                        )}
                                        onChange={(e) =>
                                            setReferenceOpacity(
                                                Number(e.target.value) / 100,
                                            )
                                        }
                                    />
                                </label>
                            </div>
                            <div className="field-row field-row--switch">
                                <CheckboxField
                                    checked={includeReferenceInExport}
                                    onChange={setIncludeReferenceInExport}
                                    label="Include reference in export"
                                    inline
                                    title="When off, Download SVG contains only paper and strokes."
                                />
                            </div>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={clearReferenceImage}
                            >
                                <Trash2 size={14} aria-hidden />
                                Remove reference
                            </button>
                        </>
                    )}
                    {!referenceImage && (
                        <p className="control-panel-hint">
                            {resizeCanvasToReferenceImage
                                ? "On upload, the canvas matches the image size and the reference fills the artboard."
                                : "Image is fitted inside the canvas (centered, aspect preserved). Draw on top with the brush."}
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Tools" icon={Brush} defaultOpen>
                    <div className="field-row">
                        <label className="field-label">Active tool</label>
                    </div>
                    <div className="artbrush-tool-grid">
                        <button
                            type="button"
                            className={`artbrush-tool-btn ${tool === "brush" ? "artbrush-tool-btn--active" : ""}`}
                            aria-pressed={tool === "brush"}
                            title="Pressure brush"
                            onClick={() => setTool("brush")}
                        >
                            <Pencil size={18} strokeWidth={2} aria-hidden />
                            <span className="artbrush-tool-btn-label">
                                Brush
                            </span>
                        </button>
                        <button
                            type="button"
                            className={`artbrush-tool-btn ${tool === "stamp" ? "artbrush-tool-btn--active" : ""}`}
                            aria-pressed={tool === "stamp"}
                            title="Object along path stamp"
                            onClick={() => setTool("stamp")}
                            disabled={parsedStampObjects.length === 0}
                        >
                            <Shapes size={18} strokeWidth={2} aria-hidden />
                            <span className="artbrush-tool-btn-label">
                                Along path
                            </span>
                        </button>
                        <button
                            type="button"
                            className={`artbrush-tool-btn ${tool === "eraser" ? "artbrush-tool-btn--active" : ""}`}
                            aria-pressed={tool === "eraser"}
                            title="Eraser"
                            onClick={() => setTool("eraser")}
                        >
                            <Eraser size={18} strokeWidth={2} aria-hidden />
                            <span className="artbrush-tool-btn-label">
                                Eraser
                            </span>
                        </button>
                    </div>
                </CollapsibleToolGroup>

                {tool === "stamp" && (
                    <CollapsibleToolGroup
                        title="Stamp object"
                        icon={Shapes}
                        defaultOpen
                    >
                        <p className="path-populate-preset-hint">
                            Preset object
                        </p>
                        <div className="path-populate-preset-chips">
                            {OBJECT_SVG_PRESETS.map((preset) => (
                                <button
                                    key={preset.id}
                                    type="button"
                                    className="generic-button path-populate-preset-chip path-populate-preset-chip--icon"
                                    title={`Use ${preset.label}`}
                                    aria-label={`Use ${preset.label}`}
                                    onClick={() =>
                                        setStampObjectFromPreset(preset.id)
                                    }
                                >
                                    <span
                                        className="path-populate-preset-chip-icon"
                                        aria-hidden
                                        dangerouslySetInnerHTML={{
                                            __html: preset.svg,
                                        }}
                                    />
                                </button>
                            ))}
                        </div>
                        <input
                            ref={stampObjectInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="visually-hidden"
                            onChange={handleStampObjectFileLoad}
                        />
                        <button
                            type="button"
                            className="tool-file-btn"
                            onClick={() => stampObjectInputRef.current?.click()}
                        >
                            <Upload size={16} aria-hidden />
                            Add object SVG
                        </button>
                        {objectSvgs.length > 0 && (
                            <div className="path-populate-object-list">
                                {objectSvgs.map((svg, index) => (
                                    <div
                                        key={`${svg.name}-${index}`}
                                        className="path-populate-object-row"
                                    >
                                        <span
                                            className="path-populate-object-name"
                                            title={svg.name}
                                        >
                                            {svg.name}
                                        </span>
                                        <button
                                            type="button"
                                            className="tool-action-btn tool-action-btn--danger"
                                            onClick={() =>
                                                removeStampObjectAt(index)
                                            }
                                        >
                                            <Trash2 size={14} aria-hidden />
                                            <span>Remove</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {objectSvgs.length > 1 && (
                            <div className="field-row">
                                <label className="field-label">
                                    Object placement mode
                                    <select
                                        value={stampObjectPlacementMode}
                                        onChange={(e) =>
                                            setStampObjectPlacementMode(
                                                e.target.value as
                                                    | "alternating"
                                                    | "random",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="alternating">
                                            Alternating
                                        </option>
                                        <option value="random">Random</option>
                                    </select>
                                </label>
                            </div>
                        )}
                        <div className="field-row">
                            <label className="field-label">
                                Perpendicular scatter (px)
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={500}
                                    step={0.5}
                                    value={stampPerpendicularScatterPx}
                                    onChange={(v) => {
                                        const n =
                                            typeof v === "number"
                                                ? v
                                                : Number(v);
                                        if (Number.isFinite(n))
                                            setStampPerpendicularScatterPx(n);
                                    }}
                                />
                            </label>
                        </div>
                        <div className="field-row">
                            <CheckboxField
                                checked={stampColorEnabled}
                                onChange={setStampColorEnabled}
                                label="Apply color options"
                                inline
                            />
                        </div>
                        {stampColorEnabled && (
                            <>
                                <div className="field-row">
                                    <label className="field-label">
                                        Color mode
                                        <select
                                            value={stampColorMode}
                                            onChange={(e) =>
                                                setStampColorMode(
                                                    e.target.value as
                                                        | "single"
                                                        | "random-per-stroke"
                                                        | "random-per-instance"
                                                        | "random-palette-per-instance",
                                                )
                                            }
                                            className="anchor-select"
                                        >
                                            <option value="single">
                                                Single color
                                            </option>
                                            <option value="random-per-stroke">
                                                Random from palette per stroke
                                            </option>
                                            <option value="random-palette-per-instance">
                                                Random from palette per instance
                                            </option>
                                            <option value="random-per-instance">
                                                Random color per instance
                                            </option>
                                        </select>
                                    </label>
                                </div>
                                {stampColorMode === "single" && (
                                    <div className="field-row artbrush-color-row">
                                        <label className="field-label-inline">
                                            <span>Stamp color</span>
                                            <input
                                                type="color"
                                                value={stampColorSingle}
                                                onChange={(e) =>
                                                    setStampColorSingle(
                                                        e.target.value,
                                                    )
                                                }
                                                className="artbrush-color-input"
                                            />
                                            <input
                                                type="text"
                                                value={stampColorSingle}
                                                onChange={(e) =>
                                                    setStampColorSingle(
                                                        e.target.value,
                                                    )
                                                }
                                                className="artbrush-color-text"
                                                spellCheck={false}
                                            />
                                        </label>
                                    </div>
                                )}
                                {(stampColorMode === "random-per-stroke" ||
                                    stampColorMode ===
                                        "random-palette-per-instance") && (
                                    <div className="field-row column">
                                        <label className="field-label">
                                            Palette colors
                                        </label>
                                        <ColorPaletteEditor
                                            colors={stampColorPalette}
                                            onChange={setStampColorPalette}
                                            minColors={1}
                                            maxColors={12}
                                            defaultNewColor="#ffffff"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        <div className="field-row">
                            <label className="field-label">
                                Spacing along path: {stampDistance}
                                <DebouncedInput
                                    type="number"
                                    min={2}
                                    max={500}
                                    step={1}
                                    value={stampDistance}
                                    onChange={(v) => {
                                        const n =
                                            typeof v === "number"
                                                ? v
                                                : Number(v);
                                        if (Number.isFinite(n))
                                            setStampDistance(n);
                                    }}
                                />
                            </label>
                        </div>
                        <div className="field-row">
                            <label className="field-label">
                                Object size: {stampObjectSize}
                                <DebouncedInput
                                    type="number"
                                    min={2}
                                    max={500}
                                    step={1}
                                    value={stampObjectSize}
                                    onChange={(v) => {
                                        const n =
                                            typeof v === "number"
                                                ? v
                                                : Number(v);
                                        if (Number.isFinite(n))
                                            setStampObjectSize(n);
                                    }}
                                />
                            </label>
                        </div>
                        <div className="field-row field-row--switch">
                            <CheckboxField
                                checked={stampPressureSizeEnabled}
                                onChange={setStampPressureSizeEnabled}
                                label="Size based on pen pressure"
                                inline
                            />
                        </div>
                        {stampPressureSizeEnabled && (
                            <div className="field-row">
                                <label className="field-label">
                                    Min size at light pressure:{" "}
                                    {Math.round(stampPressureMinFactor * 100)}%
                                    <input
                                        type="range"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={Math.round(
                                            stampPressureMinFactor * 100,
                                        )}
                                        onChange={(e) =>
                                            setStampPressureMinFactor(
                                                Number(e.target.value) / 100,
                                            )
                                        }
                                    />
                                </label>
                            </div>
                        )}
                        <div className="field-row">
                            <label className="field-label">
                                Base rotation (deg)
                                <DebouncedInput
                                    type="number"
                                    step={5}
                                    value={stampBaseRotationDeg}
                                    onChange={(v) => {
                                        const n =
                                            typeof v === "number"
                                                ? v
                                                : Number(v);
                                        if (Number.isFinite(n))
                                            setStampBaseRotationDeg(n);
                                    }}
                                />
                            </label>
                        </div>
                        <div className="field-row">
                            <label className="field-label">
                                Object rotation
                                <select
                                    value={stampRotationMode}
                                    onChange={(e) =>
                                        setStampRotationMode(
                                            e.target.value as
                                                | "align"
                                                | "random"
                                                | "random-90"
                                                | "none"
                                                | "polar"
                                                | "polar-inverted",
                                        )
                                    }
                                    className="anchor-select"
                                >
                                    <option value="align">
                                        Align to path direction
                                    </option>
                                    <option value="random">
                                        Random rotation
                                    </option>
                                    <option value="random-90">
                                        Random rotation (90deg steps)
                                    </option>
                                    <option value="none">
                                        No rotation (base only)
                                    </option>
                                    <option value="polar">
                                        Polar around point (bottom=0)
                                    </option>
                                    <option value="polar-inverted">
                                        Polar around point (top=0)
                                    </option>
                                </select>
                            </label>
                        </div>
                        {(stampRotationMode === "polar" ||
                            stampRotationMode === "polar-inverted") && (
                            <>
                                <div className="field-row">
                                    <label className="field-label">
                                        Polar point X (%)
                                        <DebouncedInput
                                            type="number"
                                            step={1}
                                            value={stampPolarRotationCenterXPct}
                                            onChange={(v) => {
                                                const n =
                                                    typeof v === "number"
                                                        ? v
                                                        : Number(v);
                                                if (Number.isFinite(n))
                                                    setStampPolarRotationCenterXPct(
                                                        n,
                                                    );
                                            }}
                                        />
                                    </label>
                                </div>
                                <div className="field-row">
                                    <label className="field-label">
                                        Polar point Y (%)
                                        <DebouncedInput
                                            type="number"
                                            step={1}
                                            value={stampPolarRotationCenterYPct}
                                            onChange={(v) => {
                                                const n =
                                                    typeof v === "number"
                                                        ? v
                                                        : Number(v);
                                                if (Number.isFinite(n))
                                                    setStampPolarRotationCenterYPct(
                                                        n,
                                                    );
                                            }}
                                        />
                                    </label>
                                </div>
                            </>
                        )}
                        <div className="field-row">
                            <CheckboxField
                                checked={stampMergeUnionOnStrokeEnd}
                                onChange={setStampMergeUnionOnStrokeEnd}
                                label="Merge instances to one path when stroke ends (boolean union)"
                                inline
                            />
                        </div>
                        {stampMergeUnionOnStrokeEnd && (
                            <p className="path-populate-merge-note">
                                Per-instance fills and gradients are not
                                preserved: each finished stroke becomes one path
                                using a single fill from the object SVG.
                            </p>
                        )}
                        <p className="control-panel-hint">
                            Draw a stroke; objects appear every spacing distance
                            along the path. Default rotation follows stroke
                            direction (align mode).
                        </p>
                    </CollapsibleToolGroup>
                )}

                {tool === "brush" && (
                    <CollapsibleToolGroup
                        title="Brush"
                        icon={Brush}
                        defaultOpen
                    >
                        <div className="field-row">
                            <label className="field-label">
                                Max brush size: {brushSize}
                                <input
                                    type="range"
                                    min={4}
                                    max={120}
                                    step={1}
                                    value={brushSize}
                                    onChange={(e) =>
                                        setBrushSize(Number(e.target.value))
                                    }
                                />
                            </label>
                        </div>
                        <div className="field-row">
                            <label className="field-label">
                                Brush strokes: {brushStrokeCount}
                                <input
                                    type="range"
                                    min={1}
                                    max={11}
                                    step={1}
                                    value={brushStrokeCount}
                                    onChange={(e) =>
                                        setBrushStrokeCount(
                                            Number(e.target.value),
                                        )
                                    }
                                />
                            </label>
                        </div>
                        <div className="field-row">
                            <label className="field-label">
                                Max spread distance (px): {brushMaxSpreadPx}
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={1000}
                                    step={1}
                                    value={brushMaxSpreadPx}
                                    onChange={(v) => {
                                        const n =
                                            typeof v === "number"
                                                ? v
                                                : Number(v);
                                        if (Number.isFinite(n))
                                            setBrushMaxSpreadPx(n);
                                    }}
                                />
                            </label>
                        </div>
                        <div className="field-row">
                            <label className="field-label">
                                Stroke smoothing: {strokeSmoothing}%
                                <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={strokeSmoothing}
                                    onChange={(e) =>
                                        setStrokeSmoothing(
                                            Number(e.target.value),
                                        )
                                    }
                                />
                            </label>
                        </div>
                        <div className="field-row">
                            <label className="field-label">
                                Min size (light pressure):{" "}
                                {Math.round(minPressureFactor * 100)}%
                                <input
                                    type="range"
                                    min={0}
                                    max={80}
                                    step={1}
                                    value={Math.round(minPressureFactor * 100)}
                                    onChange={(e) =>
                                        setMinPressureFactor(
                                            Number(e.target.value) / 100,
                                        )
                                    }
                                />
                            </label>
                        </div>
                        <p className="control-panel-hint">
                            Light pressure approaches minimum size and keeps
                            lanes closer together. Full pressure uses max brush
                            size and expands lanes toward max spread.
                        </p>
                        <p className="control-panel-hint">
                            Smoothing stabilizes stroke motion for brush and
                            stamp; higher values produce cleaner but less
                            reactive lines.
                        </p>
                    </CollapsibleToolGroup>
                )}

                {tool === "eraser" && (
                    <CollapsibleToolGroup
                        title="Eraser"
                        icon={Eraser}
                        defaultOpen
                    >
                        <div className="field-row">
                            <label className="field-label">
                                Eraser size: {eraserSize}
                                <input
                                    type="range"
                                    min={4}
                                    max={240}
                                    step={1}
                                    value={eraserSize}
                                    onChange={(e) =>
                                        setEraserSize(Number(e.target.value))
                                    }
                                />
                            </label>
                        </div>
                        <p className="control-panel-hint">
                            Removes brush strokes and stamp instances that touch
                            the eraser radius.
                        </p>
                    </CollapsibleToolGroup>
                )}

                {tool === "brush" && (
                    <CollapsibleToolGroup
                        title="Color"
                        icon={Droplet}
                        defaultOpen
                    >
                        <div className="field-row artbrush-color-row">
                            <label className="field-label-inline">
                                <span>Current color</span>
                                <input
                                    type="color"
                                    value={color}
                                    onChange={(e) =>
                                        handleColorPickerChange(e.target.value)
                                    }
                                    className="artbrush-color-input"
                                />
                                <input
                                    type="text"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    className="artbrush-color-text"
                                    spellCheck={false}
                                />
                            </label>
                        </div>
                        <div className="artbrush-recent">
                            {recentColors.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    className={`artbrush-swatch ${c.toLowerCase() === color.toLowerCase() ? "artbrush-swatch--active" : ""}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setColor(c)}
                                    title={c}
                                    aria-label={`Color ${c}`}
                                />
                            ))}
                        </div>
                    </CollapsibleToolGroup>
                )}

                <CollapsibleToolGroup title="Pen debug" icon={Bug}>
                    <CheckboxField
                        checked={showPenDebug}
                        onChange={setShowPenDebug}
                        label="Show pen debug (canvas overlay + readout)"
                        title="Pressure-sized preview circle and pointer fields."
                    />
                    {showPenDebug && (
                        <>
                            <button
                                type="button"
                                className="generic-button artbrush-pen-debug-clear"
                                onClick={clearPenSnapshot}
                            >
                                Reset readout
                            </button>
                            <PenDebugReadout s={penSnapshot} />
                        </>
                    )}
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
